import { FormBuilder, FormGroup, Validators } from "@angular/forms";

import { Observable, Subject, Subscription } from "rxjs";

import { CustomerPartyDTO } from "src/app/models/Party/customerPartyDTO";
import { EmployeePartyDTO } from "src/app/models/Party/employeePartyDTO";
import { PartyAddressDTO } from "src/app/models/Party/partyAddressDTO";
import { PartyContactDTO } from "src/app/models/Party/partyContactDTO";
import { SupplierPartyDTO } from "src/app/models/Party/supplierPartyDTO";
import { CountryDTO } from "src/app/models/Country/countryDTO";
import { PARTY_FORM_PARAMS } from "src/app/models/Party/party-form-params";
import { StatusChip } from "src/app/models/top-card/top-card.model";

import { FormUtils } from "../../utils/formUtils";
import { CHIP_STYLE, getChipStyle } from "../../utils/customTableUtils";
import { RolePermissionsService } from "../../services/role-permissions/role-permissions.service";
import { Injectable } from "@angular/core";
import { filter, switchMap, take, takeUntil, pairwise, startWith } from 'rxjs/operators';
import { PartiesService } from "src/app/shared/services/parties/parties.service";

@Injectable()

export class BaseParty {

  // Forms
  customerDataForm: FormGroup;
  employeeDataForm: FormGroup;

  //Date Format
  tenantDataSub: Subscription;
  tenantData$: Observable<any>;
  dateFormat: string;
  amountDecimalPlaces: number;

  // Permissions
  public canWrite: boolean = true;
  public canWrite$: Observable<boolean>;
  public canReadEmployees$: Observable<boolean>;
  public canReadContracts$: Observable<boolean>;
  public canReadCards$: Observable<boolean>;
  public canReadVehicles$: Observable<boolean>;
  public canReadContractMov$: Observable<boolean>;
  public canReadCurAcc$: Observable<boolean>;
  public canReadProducts$: Observable<boolean>;

  protected permissionResource: string;

  public form: FormGroup;

  protected destroy$ = new Subject<void>();

  //Party Status List
  statusList = [
    { label: "Status.Active", value: "ACTIVE" },
    { label: "Status.Inactive", value: "INACTIVE" },
    { label: "Status.Blocked", value: "BLOCKED" },
  ];

  // Party Contacts
  contactTypes = ["email", "phone", "mobile"];

  emailRegEx =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  // loaded locale
  public locale: string = localStorage.getItem("locale");
  public tenantCountryCode: string = localStorage.getItem("tenant_country_code");
  public partyCountryCode:string;

  constructor(
    public fb: FormBuilder,
    public rolePermissionsService: RolePermissionsService,
    protected partiesService: PartiesService
  ) {}

  setPermissionResource(resource: string, formReceived: FormGroup): void {
    this.permissionResource = resource;
    this.form = formReceived;
  }

  protected initBaseParty(): void {
    if (!this.permissionResource) {
      return;
    }

    this.checkPermissions(this.form)
  }

  checkPermissions(form: FormGroup): void {
    this.rolePermissionsService["roles$"]
      .pipe(
        filter((roles) => roles.length > 0),
        take(1),
        switchMap(() =>
          this.rolePermissionsService.hasPermission(this.permissionResource, "WRITE")
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((canEdit) => {
        if (!canEdit) {
          this.canWrite = false;
          this.disableFormControls(form);
        }
      });

      this.canWrite$ = this.rolePermissionsService.hasPermission(
        this.permissionResource,
        "WRITE"
      );

      this.canReadEmployees$ = this.rolePermissionsService.hasPermission(
        "EMPLOYEE_MANAGEMENT",
        "READ"
      );

      this.canReadContracts$ = this.rolePermissionsService.hasPermission(
        "CONTRACT_MANAGEMENT",
        "READ"
      );

      this.canReadCards$ = this.rolePermissionsService.hasPermission(
        "CARD_MANAGEMENT",
        "READ"
      );

      this.canReadVehicles$ = this.rolePermissionsService.hasPermission(
        "VEHICLE_MANAGEMENT",
        "READ"
      );

      this.canReadContractMov$ = this.rolePermissionsService.hasPermission(
        "CONTRACT_MOVEMENTS",
        "READ"
      );

      this.canReadCurAcc$ = this.rolePermissionsService.hasPermission(
        "CURRENT_ACCOUNTS_MANAGEMENT",
        "READ"
      );
      
      this.canReadProducts$ = this.rolePermissionsService.hasPermission(
        "PRODUCT_MANAGEMENT",
        "READ",
      );

  }

  protected disableFormControls(form: FormGroup): void {
    const fields = [
      PARTY_FORM_PARAMS.statusCode,
      PARTY_FORM_PARAMS.blockedReason,
      PARTY_FORM_PARAMS.vatin,
      PARTY_FORM_PARAMS.name,
      PARTY_FORM_PARAMS.phone,
      PARTY_FORM_PARAMS.birthday,
      PARTY_FORM_PARAMS.salutationCode,
      PARTY_FORM_PARAMS.email,
      PARTY_FORM_PARAMS.mobile,
      PARTY_FORM_PARAMS.street,
      PARTY_FORM_PARAMS.city,
      PARTY_FORM_PARAMS.postalZipCode,
      PARTY_FORM_PARAMS.countryCode,
      PARTY_FORM_PARAMS.description,
      PARTY_FORM_PARAMS.contactName,
      PARTY_FORM_PARAMS.categories,
      PARTY_FORM_PARAMS.properties,
    ];

    fields.forEach(field => form.get(field)?.disable());
  }

  loadTenantDateFomat(): void {
    this.tenantDataSub = this.tenantData$.subscribe((data: any) => {
      if (data) {
        this.dateFormat = data.dateFormat;
        this.amountDecimalPlaces = data.amountDecimalPlaces;
      }
    });
  }

  buildPartyForm() {
    return this.fb.group({
      [PARTY_FORM_PARAMS.statusCode]: ["ACTIVE", Validators.required],
      [PARTY_FORM_PARAMS.blockedReason]: [null],
      [PARTY_FORM_PARAMS.code]: [null, Validators.pattern(/^[A-Za-z0-9]+$/)],
      [PARTY_FORM_PARAMS.vatin]: [
        null, 
        {
          validators: [
            Validators.required, 
            Validators.minLength(2), 
            Validators.maxLength(20), 
            FormUtils.whitespaceBetweenValidator,
          ],
          asyncValidators: [this.partiesService.validateVatinAGT('countryCode', 'name')],
          updateOn: 'blur'
        }
      ],
      [PARTY_FORM_PARAMS.name]: [null, [Validators.required, Validators.maxLength(60), FormUtils.noWhitespaceValidator]],
      [PARTY_FORM_PARAMS.birthday]: [null],
      [PARTY_FORM_PARAMS.salutationCode]: [null],
      [PARTY_FORM_PARAMS.contactName]: [null, Validators.maxLength(45)],
      [PARTY_FORM_PARAMS.phone]: [
        null,
        [Validators.maxLength(45), FormUtils.whitespaceBetweenValidator],
      ],
      [PARTY_FORM_PARAMS.email]: [
        null,
        [Validators.maxLength(45), Validators.pattern(this.emailRegEx)],
      ],
      [PARTY_FORM_PARAMS.mobile]: [
        null,
        [Validators.maxLength(45), FormUtils.whitespaceBetweenValidator],
      ],
      [PARTY_FORM_PARAMS.description]: [null, Validators.maxLength(45)],
      [PARTY_FORM_PARAMS.street]: [null, Validators.maxLength(100)],
      [PARTY_FORM_PARAMS.postalZipCode]: [null, Validators.maxLength(45)],
      [PARTY_FORM_PARAMS.city]: [null, Validators.maxLength(45)],
      [PARTY_FORM_PARAMS.countryCode]: [this.partyCountryCode],
      [PARTY_FORM_PARAMS.categories]: this.fb.array([]),
      [PARTY_FORM_PARAMS.properties]: this.fb.array([]),
      [PARTY_FORM_PARAMS.commercialTerms]: this.fb.group({}),
    });
  }

  onStatusChanges(form: FormGroup): void {
    form
      .get(PARTY_FORM_PARAMS.statusCode)
      .valueChanges.subscribe((selectedStatus) => {
        if (selectedStatus === "BLOCKED") {
          form
            .get("blockedReason")
            .setValidators([Validators.required, Validators.maxLength(100)]);
        } else {
          form.get("blockedReason").clearValidators();
          form.get("blockedReason").updateValueAndValidity();
        }
      });
  }

  onCountryChanges(form: FormGroup): void {
    const vatinControl = form.get(PARTY_FORM_PARAMS.vatin);
    const country = form.get(PARTY_FORM_PARAMS.countryCode)?.value;

    form.get(PARTY_FORM_PARAMS.countryCode)
      ?.valueChanges
      .pipe(
        startWith(form.get(PARTY_FORM_PARAMS.countryCode)?.value),
        pairwise()
      )
      .subscribe(([prevCountry, nextCountry]) => {

        if (prevCountry !== nextCountry && this.canWrite) {
          vatinControl.enable();
        }

        form.get(PARTY_FORM_PARAMS.vatin)
          ?.updateValueAndValidity();

      });
  }

  setBasePartyForm(
    form: FormGroup,
    party: CustomerPartyDTO | EmployeePartyDTO | SupplierPartyDTO,
    partyCode: string,
    duplication: boolean,
    creation: boolean
  ) {
    this.partyCountryCode = this.getAddress(PARTY_FORM_PARAMS.countryCode, party.addresses)
    form.controls[PARTY_FORM_PARAMS.statusCode].setValue(
      party?.statusCode ?? null
    );
    form.controls[PARTY_FORM_PARAMS.blockedReason].setValue(
      party?.blockedReason ?? null
    );
    form.controls[PARTY_FORM_PARAMS.code].setValue(
      duplication ? null : partyCode ?? null
    );
    form.controls[PARTY_FORM_PARAMS.name].setValue(
      duplication ? null : party?.name ?? null
    );
    form.controls[PARTY_FORM_PARAMS.birthday].setValue(
      duplication ? null : party?.birthday ?? null
    );
    form.controls[PARTY_FORM_PARAMS.vatin].setValue(
      duplication ? null : party?.vatin ?? null
    );
    if (creation || duplication) {
      form.controls[PARTY_FORM_PARAMS.vatin].enable();
    } else if (!creation || !duplication) {
      form.controls[PARTY_FORM_PARAMS.vatin].disable();
    }
    form.controls[PARTY_FORM_PARAMS.salutationCode].setValue(
      duplication ? null : party?.salutationCode ?? null
    );
    form.controls[PARTY_FORM_PARAMS.contactName].setValue(
      duplication ? null : party?.additionalData?.contactName ?? null
    );
    form.controls[PARTY_FORM_PARAMS.phone].setValue(
      duplication
        ? null
        : this.getContact(PARTY_FORM_PARAMS.phone, party.contacts) ?? null
    );
    form.controls[PARTY_FORM_PARAMS.email].setValue(
      duplication
        ? null
        : this.getContact(PARTY_FORM_PARAMS.email, party.contacts) ?? null
    );
    form.controls[PARTY_FORM_PARAMS.mobile].setValue(
      duplication
        ? null
        : this.getContact(PARTY_FORM_PARAMS.mobile, party.contacts) ?? null
    );
    form.controls[PARTY_FORM_PARAMS.description].setValue(
      duplication
        ? null
        : this.getAddress(PARTY_FORM_PARAMS.description, party.addresses) ??
            null
    );
    form.controls[PARTY_FORM_PARAMS.street].setValue(
      duplication
        ? null
        : this.getAddress(PARTY_FORM_PARAMS.street, party.addresses) ?? null
    );
    form.controls[PARTY_FORM_PARAMS.postalZipCode].setValue(
      duplication
        ? null
        : this.getAddress(PARTY_FORM_PARAMS.postalZipCode, party.addresses) ??
            null
    );
    form.controls[PARTY_FORM_PARAMS.city].setValue(
      duplication
        ? null
        : this.getAddress(PARTY_FORM_PARAMS.city, party.addresses) ?? null
    );
    form.controls[PARTY_FORM_PARAMS.countryCode].setValue(
      duplication
        ? null
        : this.getAddress(PARTY_FORM_PARAMS.countryCode, party.addresses) ??
            null
    );
  }

  lockCode(form: FormGroup) {
    form.get(PARTY_FORM_PARAMS.code).disable();
  }

  clearFormControl(form: FormGroup, categoryControl: string): void {
    form.controls[categoryControl].setValue(null);
    form.markAsDirty();
  }

  setCustomStatusChip(customer: CustomerPartyDTO): StatusChip {
    const { statusDescription, statusCode } = customer;
    if (!statusDescription || !statusCode) {
      return;
    }

    let style: CHIP_STYLE = CHIP_STYLE.INACTIVE;

    switch (statusCode) {
      case "ACTIVE":
        style = CHIP_STYLE.SUCCESS;
        break;
      case "DISABLED":
        style = CHIP_STYLE.INACTIVE;
        break;
      case "BLOCKED":
        style = CHIP_STYLE.DANGER;
        break;
    }

    let styleSettings = getChipStyle(style);

    return {
      value: statusDescription,
      icon: styleSettings.icon,
      style: styleSettings.style,
    };
  }

  getContact(contactType: string, contacts: PartyContactDTO[]): string {
    if (!contacts || !contacts.length) {
      return;
    }
    return contacts.find(
      (contact) => contact.contactMethodCode === contactType.toUpperCase()
    )?.contact;
  }

  getAddress(addressType: string, addresses: PartyAddressDTO[]): string {
    if (!addresses || !addresses.length) {
      return;
    }
    
    const [address] = addresses;
    return address[addressType];
  }

  setFormContacts(
    form: FormGroup,
    currentParty: CustomerPartyDTO | EmployeePartyDTO | SupplierPartyDTO
  ): PartyContactDTO[] {
    const contacts = this.contactTypes.reduce((acc, cur) => {
      let code = this.getContactCode(currentParty, cur.toUpperCase());
      if (!form.controls[cur].value) {
        return acc;
      }
      return [
        ...acc,
        {
          code,
          contactMethodCode: cur.toUpperCase(),
          contact: form.controls[cur].value ?? null,
          description: cur.toUpperCase(),
        },
      ];
    }, []);
    return contacts;
  }

  getContactCode(
    currentParty: CustomerPartyDTO | EmployeePartyDTO | SupplierPartyDTO,
    contactMethod: string
  ): number {
    if (!currentParty || !contactMethod) {
      return null;
    }
    const { contacts } = currentParty;
    if (!contacts) {
      return null;
    }
    const contactCode = contacts.find(
      (contact) => contact.contactMethodCode === contactMethod
    )?.code;
    if (!contactCode) {
      return null;
    }
    return contactCode;
  }

  setFormAddresses(
    form: FormGroup,
    currentParty: CustomerPartyDTO | EmployeePartyDTO | SupplierPartyDTO
  ): PartyAddressDTO[] {
    let code = this.getAddressCode(currentParty);
    return [
      {
        code,
        description: form.controls[PARTY_FORM_PARAMS.description].value ?? "",
        street: form.controls[PARTY_FORM_PARAMS.street].value ?? "",
        postalZipCode:
          form.controls[PARTY_FORM_PARAMS.postalZipCode].value ?? "",
        city: form.controls[PARTY_FORM_PARAMS.city].value ?? "",
        countryCode: form.controls[PARTY_FORM_PARAMS.countryCode].value ?? "",
      },
    ];
  }

  getAddressCode(currentCustomer: CustomerPartyDTO): number {
    if (!currentCustomer) {
      return null;
    }
    const { addresses } = currentCustomer;
    if (!addresses || !addresses.length) {
      return null;
    }
    const [address] = addresses;
    const { code } = address;
    return code;
  }

  getSelectedCountryFlag(form: FormGroup, countriesList: CountryDTO[]) {
    if (!countriesList || !form.get(PARTY_FORM_PARAMS.countryCode).value) {
      return;
    }
    const countryFlagCode = countriesList.find(
      ({ code }) => code === form.get(PARTY_FORM_PARAMS.countryCode).value
    )?.alpha2Code;
    return countryFlagCode ? countryFlagCode.toLowerCase() : null;
  }

  setCountryDetailedDescription(country: CountryDTO) {
    return country.detailedDescription[this.locale]
      ? country.detailedDescription[this.locale]
      : country.detailedDescription["en-en"];
  }

  ngOnDestroy(): void {
  this.destroy$.next();
  this.destroy$.complete();
}
}

